import * as React from 'react';

import { Form } from '@independent-software/typeui/controls/Form';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Panel } from '@independent-software/typeui/controls/Panel';
import { DataTable } from '@independent-software/typeui/controls/DataTable';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { Checkbox } from '@independent-software/typeui/controls/Checkbox';
import { Latitude, Longitude } from '../../controls/GIS';

import { Station, StationFactory, System, SystemFactory } from '../../resource/'; 
import { List, IListState, IListProps, SearchFilter, ValueStore, Query } from '../../services/';

import { IAuth } from '../../AuthContext';
import { IconBar, Export } from '../../modules';
import { Gate } from '../../controls/Gate';
import { MapStations } from './MapStations';
import { IFilter } from '../../FilterContext';

interface IListStationsProps extends IListProps {
  stationcategoryId?: number;
  
  filter: IFilter;

  /**
   * Event is fired when a station is clicked.
   */
  onClick?: (station:Station) => void;
}

interface IListStationsState extends IListState<Station> {
  time: number;
  systems: System[];
}

class ListStations extends List<Station, IListStationsProps, IListStationsState> {
  constructor(props: IAuth & IListStationsProps) {
    super(props, StationFactory, 'name', 'asc');

    // Initialize state (list initializes its own part of the state):
    this.state = {
      ...this.state,
      time: 0,
      systems: []
    };

    if(this.props.stationcategoryId) {
      this.setFilter('stationcategory', 'eq', { id: this.props.stationcategoryId }, true);
    }       

    this.setFilter('system', 'in', this.props.filter.activeSystems.join(','));
  }

  componentDidMount() {
    this.handleSystemSearch();
  }  

  private forceMapUpdate = () => {
    // Force map component to reload markers:
    const d = new Date();
    this.setState({ time: d.getTime() });
  }

  handleSearch = (value:string) => {
    this.setFilter('q', 'like', value);
    this.forceMapUpdate();
  }  

  handleChangeSystem = (value: System) => {
    this.setFilter('system', 'eq', value);
    this.forceMapUpdate();
  }

  handleSystemSearch = (q?:string) => {
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    SystemFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ systems: res.items }));
  }  

  private handleTabChange = (index: number) => {
    ValueStore.set("list_stations", index);
  }

  render() {
    const p = this.props;

    let filter = 
    <React.Fragment>
      <Panel.Content>
        <Form.Uncontrolled hint="Type to search records">
          <SearchFilter value={this.getFilter('q', 'like')} onSearch={this.handleSearch}/>
        </Form.Uncontrolled>
      </Panel.Content>
      <Panel.Footer>
        <div style={{display: "flex", justifyContent: 'space-between'}}>
          <Gate right='can_delete_stations'>
            <Form.Uncontrolled>
              <Checkbox label="Include deleted items" name="trash" type="toggle" checked={this.getTrash()} onChange={this.handleTrash}/>
            </Form.Uncontrolled>
          </Gate>
          <Export onExport={this.handleExport}/>
        </div>
      </Panel.Footer>      
    </React.Fragment>

    return (
      <React.Fragment>
        <IconBar>
          <Panel.Icon icon="tools" width={350}>
            {filter}
          </Panel.Icon>
        </IconBar> 
        {/* small spacer to position tab content under iconbar */}
        <div style={{height: '8px', fontSize: '0px'}}></div>
        <Tabs underlined nohiddenrender active={ValueStore.get('list_stations', 0)} onTabChange={this.handleTabChange}>
          <Tabs.Pane label="List">
            <DataTable error={this.state.error} loading={this.state.loading} scrollTop={this.state.scrollTop} onScroll={this.handleScroll} data={this.state.items} onFetch={this.handleFetch} onClick={p.onClick} onOrder={this.handleOrder} order={this.getOrder()} dir={this.getDir()}>
              <DataTable.Column weight={1} label="Name" order="name" dir="asc">{(item:Station) => item.name}</DataTable.Column>
              <DataTable.Column weight={1} label="System" order="systems.name" dir="asc">{(item:Station) => item.system.name}</DataTable.Column>
              <DataTable.Column weight={4} label="Description" order="description" dir="asc">{(item:Station) => item.description}</DataTable.Column>
              <DataTable.Column weight={1} label="Latitude" order="latitude" dir="asc">{(item:Station) => <Latitude value={item.latitude}/>}</DataTable.Column>
              <DataTable.Column weight={1} label="Longitude" order="longitude" dir="asc">{(item:Station) => <Longitude value={item.longitude}/>}</DataTable.Column>
              <DataTable.Column align="right" weight={1} label="Samples" order="samples_count" dir="desc">{(item:Station) => item.samples_count}</DataTable.Column>
            </DataTable>
          </Tabs.Pane>
          <Tabs.Pane label="Map">
            <MapStations query={this.state.query} time={this.state.time} onClick={p.onClick}/>
          </Tabs.Pane>
        </Tabs>
        <Dialog.Xhr open={this.state.exportError != null} error={this.state.exportError} onClose={this.handleCloseDialog}/>
      </React.Fragment>
    );
  }
}

export { ListStations };
